import {useMemo} from 'react';

import Fuse from 'fuse.js';

import {ACTIONS_REGISTRY} from './actions-registry';
import {getContextualBoosts} from './context-awareness';
import {getFrecencyScores, getTopFrecent} from './frecency';
import {NAVIGATION_REGISTRY} from './navigation-registry';
import type {ActionItem, CommandPaletteResult, NavigationItem} from './types';

const navigationFuse = new Fuse<NavigationItem>(NAVIGATION_REGISTRY, {
    keys: [
        {name: 'label', weight: 2},
        {name: 'keywords', weight: 1.5},
        {name: 'breadcrumb', weight: 0.5},
    ],
    threshold: 0.4,
    includeScore: true,
    minMatchCharLength: 2,
});

const actionsFuse = new Fuse<ActionItem>(ACTIONS_REGISTRY, {
    keys: [
        {name: 'label', weight: 2},
        {name: 'keywords', weight: 1.5},
    ],
    threshold: 0.4,
    includeScore: true,
    minMatchCharLength: 2,
});

type CommandPaletteSearchResult = {
    results: CommandPaletteResult[];
    frecentSuggestions: NavigationItem[];
};

const MAX_NAV_RESULTS = 8; // Fetch more, then rank & trim
const MAX_ACTION_RESULTS = 5;
const MAX_FRECENT_SUGGESTIONS = 6;

// Limits after ranking
const DISPLAY_NAV_LIMIT = 5;
const DISPLAY_ACTION_LIMIT = 3;

export function useCommandPaletteSearch(query: string): CommandPaletteSearchResult {
    const frecencyScores = useMemo(() => getFrecencyScores(), []);
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

    const frecentSuggestions = useMemo(() => {
        const navMap = new Map(NAVIGATION_REGISTRY.map((item) => [item.id, item]));
        const contextBoosts = getContextualBoosts(currentPath);

        // Start with frecent items
        const topFrecentIds = getTopFrecent(MAX_FRECENT_SUGGESTIONS);
        const frecentItems = topFrecentIds
            .map((id) => navMap.get(id))
            .filter(Boolean) as NavigationItem[];

        // If we have fewer than MAX items, fill with contextual suggestions
        if (frecentItems.length < MAX_FRECENT_SUGGESTIONS) {
            const existingIds = new Set(frecentItems.map((item) => item.id));
            const contextItems = contextBoosts
                .filter((id) => !existingIds.has(id))
                .map((id) => navMap.get(id))
                .filter(Boolean) as NavigationItem[];

            // Interleave: frecent first, then contextual fills
            for (const item of contextItems) {
                if (frecentItems.length >= MAX_FRECENT_SUGGESTIONS) break;
                frecentItems.push(item);
            }
        }

        return frecentItems;
    }, [currentPath]);

    const results = useMemo(() => {
        if (!query || query.trim().length === 0) {
            return [];
        }

        const queryLower = query.toLowerCase().trim();

        // Navigation results — fetch extra, then re-rank with frecency boost
        const rawNavResults = navigationFuse.search(query, {limit: MAX_NAV_RESULTS});
        const rankedNavResults = rawNavResults
            .map((result) => {
                const fuseScore = result.score ?? 1;
                const frecencyBoost = frecencyScores.get(result.item.id) || 0;
                // Exact prefix match on label gets a big boost
                const prefixBoost = result.item.label.toLowerCase().startsWith(queryLower)
                    ? 0.3
                    : 0;
                // Combined score (lower is better for fuse, so we subtract boosts)
                const combinedScore = fuseScore - frecencyBoost * 0.1 - prefixBoost;

                return {item: result.item, score: combinedScore};
            })
            .sort((a, b) => a.score - b.score)
            .slice(0, DISPLAY_NAV_LIMIT);

        // Action results — Fuse.js + prefix matching for long queries
        // When the user types "generate report for Where are my top leads...",
        // Fuse.js fails to match because the query is too long. We supplement
        // with a prefix check: if the query starts with any action keyword,
        // force-include that action.
        const rawActionResults = actionsFuse.search(query, {limit: MAX_ACTION_RESULTS});
        const fuseActionIds = new Set(rawActionResults.map((r) => r.item.id));

        // Prefix-match: check if query starts with any action's keyword
        const prefixMatches: {item: ActionItem; score: number}[] = [];
        for (const action of ACTIONS_REGISTRY) {
            if (fuseActionIds.has(action.id)) continue; // already matched by Fuse
            const matched = action.keywords.some((kw) => queryLower.startsWith(kw.toLowerCase()));
            if (matched) {
                // Give it a reasonable score (better than threshold, worse than exact)
                prefixMatches.push({item: action, score: 0.15});
            }
        }

        const rankedActionResults = rawActionResults
            .map((result) => {
                const fuseScore = result.score ?? 1;
                const prefixBoost = result.item.label.toLowerCase().startsWith(queryLower)
                    ? 0.3
                    : 0;
                const combinedScore = fuseScore - prefixBoost;

                return {item: result.item, score: combinedScore};
            })
            .concat(prefixMatches)
            .sort((a, b) => a.score - b.score)
            .slice(0, DISPLAY_ACTION_LIMIT);

        // Build merged list
        const merged: CommandPaletteResult[] = [];

        for (const {item} of rankedNavResults) {
            merged.push({type: 'navigation', item});
        }

        for (const {item} of rankedActionResults) {
            merged.push({type: 'action', item});
        }

        return merged;
    }, [query, frecencyScores]);

    return {
        results,
        frecentSuggestions,
    };
}

/**
 * Group results by their category for rendering sections.
 */
export function groupResults(results: CommandPaletteResult[]): {
    navigation: CommandPaletteResult[];
    actions: CommandPaletteResult[];
} {
    const navigation: CommandPaletteResult[] = [];
    const actions: CommandPaletteResult[] = [];

    for (const result of results) {
        switch (result.type) {
            case 'navigation':
                navigation.push(result);
                break;
            case 'action':
                actions.push(result);
                break;
        }
    }

    return {navigation, actions};
}
